// Utilitários compartilhados pelos filtros avançados do back-office.
// A ideia é declarar os filtros uma vez (schema) e derivar daí: os inputs, os
// chips de "filtros ativos", os parâmetros da API e a persistência na URL.

export const FIELD_TYPES = {
  SEARCH: "search",
  TEXT: "text",
  SELECT: "select",
  MULTISELECT: "multiselect",
  BOOLEAN: "boolean",
  DATE_RANGE: "daterange",
  NUMBER_RANGE: "numberrange",
};

export const isEmptyValue = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

/** Todas as chaves de `values` que um campo controla. */
export const fieldKeys = (field) => {
  if (field.type === FIELD_TYPES.DATE_RANGE || field.type === FIELD_TYPES.NUMBER_RANGE) {
    return [field.fromKey, field.toKey].filter(Boolean);
  }
  return [field.key].filter(Boolean);
};

/** Estado inicial vazio a partir do schema. */
export const emptyValues = (fields = []) =>
  fields.reduce((acc, field) => {
    fieldKeys(field).forEach((key) => {
      acc[key] = field.type === FIELD_TYPES.MULTISELECT ? [] : "";
    });
    return acc;
  }, {});

/** Converte o estado dos filtros nos query params esperados pela API. */
export const buildQueryParams = (fields = [], values = {}) => {
  const params = {};
  fields.forEach((field) => {
    fieldKeys(field).forEach((key) => {
      const value = values[key];
      if (isEmptyValue(value)) return;
      params[key] = Array.isArray(value) ? value.join(",") : value;
    });
  });
  return params;
};

/** Quantos filtros estão realmente preenchidos (para o badge do cabeçalho). */
export const countActiveFilters = (fields = [], values = {}) =>
  fields.reduce((total, field) => {
    const filled = fieldKeys(field).some((key) => !isEmptyValue(values[key]));
    return filled ? total + 1 : total;
  }, 0);

const optionLabel = (options = [], value) => {
  const found = options.find((option) => String(option.value) === String(value));
  return found ? found.label : value;
};

/** Chips descrevendo cada filtro ativo, com a ação de remoção já embutida. */
export const buildActiveChips = (fields = [], values = {}) => {
  const chips = [];
  fields.forEach((field) => {
    const keys = fieldKeys(field);
    const hasValue = keys.some((key) => !isEmptyValue(values[key]));
    if (!hasValue) return;

    if (field.type === FIELD_TYPES.MULTISELECT) {
      const selected = values[field.key] || [];
      const labels = selected.map((value) => optionLabel(field.options, value));
      const preview =
        labels.length > 2
          ? `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`
          : labels.join(", ");
      chips.push({ id: field.key, label: field.label, value: preview, keys });
      return;
    }

    if (field.type === FIELD_TYPES.DATE_RANGE) {
      const from = values[field.fromKey];
      const to = values[field.toKey];
      const text =
        from && to
          ? `${from} → ${to}`
          : from
          ? `a partir de ${from}`
          : `até ${to}`;
      chips.push({ id: field.fromKey, label: field.label, value: text, keys });
      return;
    }

    if (field.type === FIELD_TYPES.NUMBER_RANGE) {
      const from = values[field.fromKey];
      const to = values[field.toKey];
      const text =
        from !== "" && from !== undefined && to !== "" && to !== undefined
          ? `${from} a ${to}`
          : from !== "" && from !== undefined
          ? `≥ ${from}`
          : `≤ ${to}`;
      chips.push({ id: field.fromKey, label: field.label, value: text, keys });
      return;
    }

    chips.push({
      id: field.key,
      label: field.label,
      value:
        field.type === FIELD_TYPES.SELECT || field.type === FIELD_TYPES.BOOLEAN
          ? optionLabel(field.options, values[field.key])
          : values[field.key],
      keys,
    });
  });
  return chips;
};

/** Serializa o estado dos filtros para a query string da página. */
export const valuesToSearchParams = (values = {}) => {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (isEmptyValue(value)) return;
    params.set(key, Array.isArray(value) ? value.join(",") : value);
  });
  return params;
};

/** Lê o estado dos filtros de volta da query string, respeitando o schema. */
export const searchParamsToValues = (search, fields = []) => {
  const params = new URLSearchParams(search);
  const values = emptyValues(fields);
  fields.forEach((field) => {
    fieldKeys(field).forEach((key) => {
      const raw = params.get(key);
      if (raw === null) return;
      values[key] =
        field.type === FIELD_TYPES.MULTISELECT
          ? raw.split(",").filter(Boolean)
          : raw;
    });
  });
  return values;
};

export const YES_NO_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "true", label: "Sim" },
  { value: "false", label: "Não" },
];
